import type { ReactNode } from 'react';
import { LabeledSearchableSelectField } from '@ooh/ui/components/labeled-searchable-select-field';
import type { AvailabilityTimeEncoding } from '@ooh/contracts';
import { MANUALLY_SELECTABLE_ENCODINGS } from '../lib/manual-encodings';

export interface TimeEncodingSelectorLabels {
  label: string;
  /** Only the keys actually offered (see `encodingOptions`) need an entry. */
  options: Partial<Record<AvailabilityTimeEncoding, string>>;
}

export interface TimeEncodingSelectorProps {
  value: AvailabilityTimeEncoding;
  canOverride: boolean;
  onChange: (next: AvailabilityTimeEncoding) => void;
  labels: TimeEncodingSelectorLabels;
  /**
   * Which encodings the operator may switch to. Defaults to
   * `MANUALLY_SELECTABLE_ENCODINGS` (global/date_range/period_label_map) —
   * the 3 the generic fallback scorer's low-confidence guess can land on,
   * which is the only override path currently wired into
   * `usePreviewProfileEditor`. Pass a wider list including `'month'` to also
   * offer manual from-scratch period-column construction (see
   * `MonthPeriodColumnsTable`'s `onAddRow`/`onRemoveRow`, CU-86cb1npz2).
   */
  encodingOptions?: readonly AvailabilityTimeEncoding[];
  testIdPrefix?: string;
  /**
   * Optional content rendered inline next to the selector's label — e.g. a
   * `TimeEncodingHelpDialog` trigger. Threaded through as a slot rather than
   * placed by each caller so the affordance renders consistently wherever
   * this selector is used (mirrors `SourceFileSummaryBar`'s `noteSlot`).
   */
  labelSlot?: ReactNode;
}

/**
 * Manual escape hatch for a low-confidence availability-import resolution:
 * lets the operator re-point the file at a different encoding instead of
 * being stuck with whatever the generic fallback scorer guessed. Also the
 * entry point for manually building a `month` period-column matrix from
 * scratch when the file's period headers don't parse automatically (e.g.
 * Roman-numeral month abbreviations) — pass that encoding via
 * `encodingOptions` (see that prop's docstring).
 */
export function TimeEncodingSelector({
  value,
  canOverride,
  onChange,
  labels,
  encodingOptions = MANUALLY_SELECTABLE_ENCODINGS,
  testIdPrefix = 'preview',
  labelSlot,
}: TimeEncodingSelectorProps) {
  if (!canOverride) return null;

  const options = encodingOptions.map((encoding) => ({
    value: encoding,
    label: labels.options[encoding] ?? encoding,
  }));

  return (
    <LabeledSearchableSelectField
      id={`${testIdPrefix}-time-encoding-select`}
      label={labels.label}
      options={options}
      value={value}
      onChange={(next) => onChange(next as AvailabilityTimeEncoding)}
      testId={`${testIdPrefix}-time-encoding-select`}
      labelSlot={labelSlot}
    />
  );
}
